import { getPixelValue } from '@lemonade-hq/uniclient-theme';
import {
  ITEM_HEIGHT,
  LEVEL_ONE_ANIMATION_DELAY,
  LEVEL_TWO_GAP,
  LINE_ANIMATION_DURATION,
  SUB_ITEMS_OPACITY_TRANSITION_DURATION,
  SUB_ITEMS_WRAPPER_EXPANSION_DURATION,
  TIME_TO_MAKE_ANIMATION_LOOK_GOOD,
} from './constants';
import type { AnimationProgressProps } from './types';

export const completedHeight = ({
  numOfSubItems,
  currentSubItemIndex,
  numOfSubSubItems,
  currentSubSubItemIndex,
}: AnimationProgressProps): string => {
  let totalHeight = 0;
  const hasSubItems = numOfSubItems > 0 && currentSubItemIndex != null;
  const hasSubSubItems = numOfSubSubItems > 0 && currentSubSubItemIndex != null;

  if (hasSubItems) {
    for (let i = 0; i < currentSubItemIndex; i++) {
      totalHeight += ITEM_HEIGHT + getPixelValue(LEVEL_TWO_GAP);
    }

    totalHeight += ITEM_HEIGHT;

    if (hasSubSubItems && numOfSubSubItems > 0 && currentSubSubItemIndex >= 0) {
      totalHeight += (currentSubSubItemIndex + 1) * (ITEM_HEIGHT + getPixelValue(LEVEL_TWO_GAP));
    }
  }

  return `${totalHeight}px`;
};

export const getLineProgressDelay = ({
  isBackwards = false,
  isExpanding = false,
  level = 1,
  isLastItem = false,
}: AnimationProgressProps): number => {
  if ((isBackwards && !isLastItem) || level === 1 || !isExpanding) {
    return 0;
  }

  if (level === 2) {
    return SUB_ITEMS_WRAPPER_EXPANSION_DURATION + SUB_ITEMS_OPACITY_TRANSITION_DURATION;
  }

  return SUB_ITEMS_WRAPPER_EXPANSION_DURATION;
};

export const getAnimationState = (completed: boolean, current: boolean): string[] | string => {
  if (current) return ['open', 'currentFade'];
  if (completed) return ['open', 'completedFade'];

  return 'closed';
};

export const getInnerDotAnimationState = (completed: boolean, current: boolean): string[] | string => {
  if (current) return 'open';
  if (completed) return ['closed', 'fade'];

  return 'open';
};

export const getSubItemDotAnimationState = (completed: boolean, current: boolean): string[] | string => {
  if (current) return ['open', 'currentFade'];
  if (completed) return ['open', 'completedFade'];

  return 'open';
};

interface GetDotCurrentFadeDelayProps {
  readonly isBackwards: boolean;
  readonly previousSubItemHasSubSubItems: boolean;
  readonly isFirst: boolean;
}

export const getDotCurrentFadeDelay = ({
  isBackwards,
  previousSubItemHasSubSubItems,
  isFirst,
}: GetDotCurrentFadeDelayProps): number => {
  if (isBackwards) return 0;
  if (previousSubItemHasSubSubItems) {
    // Multiply by 2 because the line animation is going up and then down
    return LINE_ANIMATION_DURATION * 2;
  }

  if (isFirst) {
    return SUB_ITEMS_WRAPPER_EXPANSION_DURATION + LINE_ANIMATION_DURATION - TIME_TO_MAKE_ANIMATION_LOOK_GOOD;
  }

  return LINE_ANIMATION_DURATION;
};

export const getTextFadeDelay = ({
  level,
  previousSubItemHasSubSubItems,
  isExpanding,
  isBackwards,
}: Pick<AnimationProgressProps, 'isBackwards' | 'isExpanding' | 'level'> & {
  readonly previousSubItemHasSubSubItems?: boolean;
}): number => {
  if (level === 1) {
    return LEVEL_ONE_ANIMATION_DELAY;
  }

  if (previousSubItemHasSubSubItems) {
    return SUB_ITEMS_WRAPPER_EXPANSION_DURATION + LINE_ANIMATION_DURATION * 2;
  }

  if (isExpanding && !isBackwards) {
    return SUB_ITEMS_WRAPPER_EXPANSION_DURATION + LINE_ANIMATION_DURATION;
  }

  return LINE_ANIMATION_DURATION;
};

export const getLineAnimationState = (
  completed: boolean,
  delayedCompleted: boolean,
  currentSubItemIndex: number,
): string => {
  if (completed) return 'completed';
  if (delayedCompleted) return 'completedFade';

  return currentSubItemIndex > -1 ? 'progress' : 'initial';
};
